import React from 'react'
import moment from 'moment'
import firebase from 'firebase/app'
import { meanBy, filter, size, min, max, mean, sum, sumBy, orderBy } from 'lodash'
import { Segment, Button, Table, Label, Header } from 'semantic-ui-react'

import BatteryStatus from '../../../helpers/BatteryStatus'
import getEarnStatusColor from '../../../helpers/EarnStatus'
import RealTimeTracker from '../../../components/common/tracker'
import DateRangeSearch from './DateRangeSearch'

const MAX_ACCEPTANCE_SCORE = 70
const MAX_HOUR_SCORE = 300

class ActivitiesTab extends React.Component {
  state = {
    tab: 'activities',
    from: moment()
      .startOf('day')
      .format('x'),
    to: moment()
      .endOf('day')
      .format('x'),

    dates: [],
    orders: [],
    activities: [],
    workingDurations: [],
    loading: false,
  }

  componentDidMount() {
    this.getActivities()
  }

  getActivities = async () => {
    const { driverUid } = this.props
    const { from, to } = this.state

    this.setState({ loading: true })

    const activityRef = firebase
      .database()
      .ref(`muuver_activities/${driverUid}`)
      .orderByChild('created_at')
      .startAt(parseInt(from, 10))
      .endAt(parseInt(to, 10))
      .once('value')

    const performanceRef = firebase
      .database()
      .ref(`deliverer_orders/${driverUid}`)
      .orderByChild('created_at')
      .startAt(parseInt(from, 10))
      .endAt(parseInt(to, 10))
      .once('value')

    try {
      const results = await Promise.all([activityRef, performanceRef])
      const activitiesSnapshot = results[0]
      const ordersSnapshot = results[1]
      const activities = Object.values(activitiesSnapshot.val() || {})
      const orders = this.sortOrders(ordersSnapshot)

      this.setState({ activities, orders, loading: false }, this.setDates)
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err)
      this.setState({ loading: false })
    }
  }

  getOrderAnalyticByDate = date => {
    const { orders } = this.state
    const orderDate = moment(date, 'YYYY-MM-DD')

    const ordersByDate = orders.filter(order =>
      moment(parseInt(order.created_at, 10)).isSame(orderDate, 'day')
    )
    const orderCount = ordersByDate.length

    let deliveredOrderCount = 0
    let rejectedOrderCount = 0
    let maximumCountedOrder = 0
    let earnPerDay = 0

    for (let index = 0; index < orderCount; index += 1) {
      const order = ordersByDate[index]

      if (order.status === 'delivered') {
        deliveredOrderCount += 1
        maximumCountedOrder += 1

        const feeRate = order.fee_rate || 25
        const fullDeliveryFee = order.delivery_fee
        const earnFee = Number(fullDeliveryFee * (1 - feeRate / 100))

        earnPerDay += earnFee
      } else if (order.status === 'rejected') {
        rejectedOrderCount += 1

        const foundDistance = Number(order.distance || 0) // distance between driver and merchant
        const findingRadius = Number(order.finding_radius || 0)

        const isCannotNotified = order.rejected_reason === 'cannot-notify'
        const isSwitchedDriver = order.rejected_reason === 'switch-driver'

        const isInRadius = foundDistance < findingRadius

        if (isInRadius && (!isCannotNotified || !isSwitchedDriver)) maximumCountedOrder += 1
      }
    }
    const acceptance =
      maximumCountedOrder > 0
        ? parseInt((MAX_ACCEPTANCE_SCORE * deliveredOrderCount) / maximumCountedOrder, 10)
        : 0

    return {
      acceptance,
      orderCount,
      deliveredOrderCount,
      rejectedOrderCount,
      earnPerDay: Number(parseFloat(earnPerDay).toFixed(2)),
    }
  }

  getOrderAnalytic = () => {
    const { workingDurations } = this.state
    const allOrderArr = []
    const successOrderArr = []
    const onlineDurationArr = []

    workingDurations.forEach(({ date, duration }) => {
      const { orderCount, acceptance, deliveredOrderCount } = this.getOrderAnalyticByDate(date)
      successOrderArr.push(deliveredOrderCount)
      onlineDurationArr.push(this.generateOverallScore(duration, acceptance))

      if (orderCount > 0) {
        allOrderArr.push(orderCount)
      }
    })

    const maxOrder = max(allOrderArr)
    const minOrder = min(allOrderArr)
    const avgOrder = mean(allOrderArr)
    const orderCount = sum(successOrderArr)
    const avgOverallScore = mean(onlineDurationArr)

    return {
      maxOrder,
      minOrder,
      avgOverallScore: parseInt(avgOverallScore, 10),
      avgOrder: parseInt(avgOrder, 10),
      orderCount,
    }
  }

  getWorkingDuration = () => {
    const { workingDurations } = this.state

    if (workingDurations.length === 0) return '0 hours'

    const workingDuration = sumBy(workingDurations, 'duration')

    return moment.duration(workingDuration, 'milliseconds').format('h [hrs] m [min]')
  }

  getRegularOnline = () => {
    const { activities } = this.state
    const onlineData = {}

    if (activities.length > 0) {
      activities.forEach(activity => {
        if (activity.online && activity.offline && activity.completed_pair) {
          const onlineTimestamp = parseInt(activity.online.timestamp, 10)
          const onlineAt = parseInt(moment(onlineTimestamp).format('H'), 10)

          if (!onlineData[onlineAt]) {
            onlineData[onlineAt] = {
              count: 1,
              onlineAt,
            }
          } else {
            onlineData[onlineAt] = {
              count: onlineData[onlineAt].count + 1,
              onlineAt,
            }
          }
        }
      })

      if (size(onlineData) > 0) {
        const regularOnlineAt = orderBy(onlineData, ['count', 'onlineAt'], ['desc', 'asc'])[0]

        return moment(regularOnlineAt.onlineAt.toString(), 'H').format('LT')
      }
    }

    return '--:--'
  }

  getActivityInfo = activity => {
    if (activity.online && activity.offline && activity.completed_pair) {
      const { orders } = this.state

      const startAt = moment(parseInt(activity.online.timestamp, 10))
      const endAt = moment(parseInt(activity.offline.timestamp, 10))

      const durationInMillis = moment.duration(endAt.diff(startAt, 'milliseconds')).as('milliseconds')
      const durationInWord = moment.duration(durationInMillis, 'milliseconds').format('h [hrs] m [min]')

      let successOrderCount = 0
      let allOrderCount = 0

      for (let index = 0; index < orders.length; index += 1) {
        const order = orders[index]

        if (moment(order.created_at).isBetween(startAt, endAt)) {
          allOrderCount += 1

          if (order.status === 'delivered') successOrderCount += 1
        }
      }

      return {
        duration: durationInWord,
        order: `${successOrderCount}/${allOrderCount}`,
      }
    }

    return {}
  }

  sortOrders = (orders, status) => {
    const sortedOrders = []

    orders.forEach(snapshot => {
      const order = snapshot.val()
      const { key } = snapshot

      if (!status || status === order.status) {
        sortedOrders.push({ ...order, key })
      }
    })

    return sortedOrders
  }

  setDates = () => {
    const { from, to } = this.state
    const startFrom = moment(parseInt(from, 10))
    const endAt = moment(parseInt(to, 10))
    const dates = []

    while (startFrom.isSameOrBefore(endAt, 'day')) {
      dates.push(startFrom.format('YYYY-MM-DD'))
      startFrom.add(1, 'days')
    }

    if (size(dates) > 0) {
      this.setState({ dates }, this.performanceEachDay)
    }
  }

  generateOverallScore = (duration, acceptance) => {
    let durationInMinute = parseInt(moment.duration(duration, 'milliseconds').as('minutes'), 10)
    durationInMinute = durationInMinute > MAX_HOUR_SCORE ? MAX_HOUR_SCORE : durationInMinute
    const workingScore = parseInt((30 * durationInMinute) / MAX_HOUR_SCORE, 10)
    const acceptanceRate = parseInt(acceptance, 10)
    const score = parseInt(workingScore + acceptanceRate, 10)
    return score
  }

  performanceEachDay = () => {
    const { dates, activities } = this.state
    const workingDurations = []

    dates.forEach(d => {
      const date = moment(d, 'YYYY-MM-DD')
      let eachDateDuration = 0

      if (size(activities) > 0) {
        const activitiesEachDay = activities.filter(
          activity =>
            moment(parseInt(activity.created_at, 10)).isSame(date, 'day') &&
            activity.online &&
            activity.offline &&
            activity.completed_pair
        )

        activitiesEachDay.forEach(activity => {
          const startAt = moment(parseInt(activity.online.timestamp, 10))
          const endAt = moment(parseInt(activity.offline.timestamp, 10))

          const duration = moment.duration(endAt.diff(startAt, 'milliseconds')).as('milliseconds')
          eachDateDuration += duration
        })
      }

      workingDurations.push({
        date,
        duration: eachDateDuration,
      })
    })

    this.setState({ workingDurations })
  }

  renderActivityRow = activity => {
    const activityInfo = this.getActivityInfo(activity)

    return (
      <Table.Row>
        <Table.Cell textAlign="center">
          {moment(parseInt(activity.created_at, 10)).format('hh:mm a')}
          <br />
          {moment(parseInt(activity.created_at, 10)).format('DD-MMM-YYYY')}
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label style={{ margin: 0, width: 120 }} color={activity.completed_pair ? 'green' : 'red'}>
            {activity.completed_pair ? 'Completed' : 'Uncompleted'}
          </Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          {activity.online && (
            <div>
              {moment(parseInt(activity.online.timestamp, 10)).format('hh:mm a')}
              <br />
              {moment(parseInt(activity.online.timestamp, 10)).format('DD-MMM-YYYY')}
            </div>
          )}
        </Table.Cell>
        <Table.Cell textAlign="center">
          {activity.offline && (
            <div>
              {moment(parseInt(activity.offline.timestamp, 10)).format('hh:mm a')}
              <br />
              {moment(parseInt(activity.offline.timestamp, 10)).format('DD-MMM-YYYY')}
            </div>
          )}
        </Table.Cell>
        <Table.Cell textAlign="center">{activityInfo.order}</Table.Cell>
        <Table.Cell textAlign="center">
          {activityInfo.duration && (
            <Label color="black" style={{ margin: 0, width: 120 }}>
              {activityInfo.duration}
            </Label>
          )}
        </Table.Cell>
      </Table.Row>
    )
  }

  renderActivityRows = () => {
    const { activities } = this.state

    if (activities.length > 0) {
      return activities.map(this.renderActivityRow).reverse()
    }

    return (
      <Table.Row>
        <Table.Cell colSpan={5} textAlign="center">
          No results
        </Table.Cell>
      </Table.Row>
    )
  }

  renderPerformanceRow = (value, index) => {
    const { date, duration } = value
    const { acceptance, deliveredOrderCount, orderCount, earnPerDay } = this.getOrderAnalyticByDate(date)

    const formattedDate = moment(date, 'YYYY-MM-DD').format('DD-MMM-YYYY')
    const durationPerDay = moment.duration(duration, 'milliseconds').format('h [hrs] m [min]')
    const scoring = this.generateOverallScore(duration, acceptance)

    return (
      <Table.Row key={index}>
        <Table.Cell textAlign="center">
          <Header as="h5">{formattedDate}</Header>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label color={duration > 0 ? 'black' : 'red'} style={{ margin: 0, width: 120 }}>
            {durationPerDay}
          </Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Header as="h5">
            {deliveredOrderCount}/{orderCount}
          </Header>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label color={BatteryStatus(acceptance, true).color}>{acceptance} %</Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label color={getEarnStatusColor(earnPerDay)}>$ {earnPerDay}</Label>
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Label color={BatteryStatus(scoring, true).color}>{scoring} %</Label>
        </Table.Cell>
      </Table.Row>
    )
  }

  renderPerformanceRows = () => {
    const { workingDurations } = this.state

    if (workingDurations.length > 0) {
      return workingDurations.map(this.renderPerformanceRow)
    }

    return (
      <Table.Row>
        <Table.Cell colSpan={5} textAlign="center">
          No results
        </Table.Cell>
      </Table.Row>
    )
  }

  render() {
    const { tab, from, to, loading, workingDurations } = this.state

    const orderAnalytic = this.getOrderAnalytic()

    let avgWorking = '--:--'

    if (workingDurations.length > 0) {
      const excludeDayOff = filter(workingDurations, value => value.duration > 0)
      const avgDuration = meanBy(excludeDayOff, 'duration')
      avgWorking = moment.duration(avgDuration, 'milliseconds').format('h [hrs] m [min]')
    }

    const startFrom = moment(parseInt(from, 10)).format('YYYY-MM-DD')
    const endAt = moment(parseInt(to, 10)).format('YYYY-MM-DD')

    return (
      <div>
        <div className="ui equal width grid">
          <div className="column">
            <RealTimeTracker
              label="Working Duration"
              icon="time"
              actualValue={this.getWorkingDuration()}
              loaded
            />
          </div>
          <div className="column">
            <RealTimeTracker label="AVG Working duration" icon="motorcycle" actualValue={avgWorking} loaded />
          </div>
          <div className="column">
            <RealTimeTracker
              label="Regular Online"
              icon="sign in"
              actualValue={this.getRegularOnline()}
              loaded
            />
          </div>
        </div>

        <div className="ui equal width grid" style={{ marginTop: 0 }}>
          <div className="column">
            <RealTimeTracker
              label="Maximum Orders"
              icon="tasks"
              actualValue={orderAnalytic.maxOrder}
              loaded
            />
          </div>
          <div className="column">
            <RealTimeTracker
              label="Minimum Orders"
              icon="minus"
              actualValue={orderAnalytic.minOrder}
              loaded
            />
          </div>
          <div className="column">
            <RealTimeTracker label="AVG Orders" icon="balance" actualValue={orderAnalytic.avgOrder} loaded />
          </div>
        </div>

        <div className="ui equal width grid" style={{ marginTop: 0 }}>
          <div className="column">
            <RealTimeTracker
              label="Order Count"
              icon="hashtag"
              actualValue={orderAnalytic.orderCount}
              loaded
            />
          </div>

          <div className="column">
            <RealTimeTracker
              label="AVG Scores"
              icon="certificate"
              actualValue={orderAnalytic.avgOverallScore}
              loaded
            />
          </div>
        </div>
        <Segment color="black" style={{ marginBottom: 20 }}>
          <div>
            <Button.Group>
              <Button secondary={tab === 'activities'} onClick={() => this.setState({ tab: 'activities' })}>
                Activities
              </Button>
              <Button.Or />
              <Button secondary={tab === 'date-list'} onClick={() => this.setState({ tab: 'date-list' })}>
                Performance
              </Button>
            </Button.Group>
          </div>

          <Segment>
            <DateRangeSearch
              start={startFrom}
              end={endAt}
              loading={loading}
              onButtonClick={this.getActivities}
              onStartChange={value => this.setState({ from: value })}
              onEndChange={value => this.setState({ to: value })}
            />
          </Segment>

          {tab === 'activities' ? (
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Created at
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Online
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Offline
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Orders
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Duration
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{this.renderActivityRows()}</Table.Body>
            </Table>
          ) : (
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Date
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Duration
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Order
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Acceptance
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Earn
                  </Table.HeaderCell>
                  <Table.HeaderCell style={{ width: '16%' }} textAlign="center">
                    Score
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{this.renderPerformanceRows()}</Table.Body>
            </Table>
          )}
        </Segment>
      </div>
    )
  }
}

export default ActivitiesTab



// WEBPACK FOOTER //
// ./src/containers/drivers/detail/ActivitiesTab.js